/** CSGPolygon3D's solid, exposed to the boolean evaluator. */
import { csgSolid } from '../../../../r3f/csg/csgRegistration';
import { transform3DToMatrix } from '../../../../r3f/nodeTreeTransforms';
import { tessellateCurve3D } from '../../../../resources/curves/curve3d/index.js';
import { buildCsgPolygonBrush } from './polygonGeometry';
import { PolygonMode } from '../../../../godot/scene/csg';
function pathPlan(p) {
    if (p.mode !== PolygonMode.PATH || !p.resolvedPath)
        return null;
    const sampler = tessellateCurve3D(p.resolvedPath.curvePoints);
    if (sampler.length <= 0)
        return null;
    return {
        sampler,
        baseMatrix: p.resolvedPath.baseTransform ? transform3DToMatrix(p.resolvedPath.baseTransform) : null,
        // `Curve3D.point_count`, which PATH_INTERVAL_SUBDIVIDE counts in.
        pointCount: p.resolvedPath.curvePoints.length,
    };
}
export const csgPolygon3DGeometry = (properties) => {
    const p = properties;
    const brush = buildCsgPolygonBrush({
        polygon: p.polygon,
        mode: p.mode,
        depth: p.depth,
        spinDegrees: p.spinDegrees,
        spinSides: p.spinSides,
        smoothFaces: p.smoothFaces,
        flipFaces: p.flipFaces,
        pathIntervalType: p.pathIntervalType,
        pathInterval: p.pathInterval,
        pathSimplifyAngle: p.pathSimplifyAngle,
        pathRotation: p.pathRotation,
        pathRotationAccurate: p.pathRotationAccurate,
        pathContinuousU: p.pathContinuousU,
        pathUDistance: p.pathUDistance,
        pathJoined: p.pathJoined,
        path: pathPlan(p),
    });
    return csgSolid(brush, [p.materialPath]);
};
/**
 * Full precision, deliberately: rounding would collide two nearby handles, positions or
 * basis rows into one key and serve the wrong solid, where a long key only costs bytes.
 */
const vec3Key = (v) => `${v.x},${v.y},${v.z}`;
function transformKey(t) {
    return t ? [t.basis_x, t.basis_y, t.basis_z, t.origin].map(vec3Key).join(',') : '';
}
export function csgPolygon3DGeometryKey(properties) {
    const p = properties;
    // The resolved curve is part of the shape, so its points belong in the key; the pass
    // builds a new `resolvedPath` on every reparse, so identity alone would never match. Both
    // Bézier handles ride along because `tessellateCurve3D` consumes them, so a dragged
    // tangent is a shape change with every position left untouched.
    const curve = p.resolvedPath
        ? p.resolvedPath.curvePoints
            .map((c) => `${vec3Key(c.in)}/${vec3Key(c.out)}/${vec3Key(c.position)}`)
            .join(';')
        : '';
    // Serialised unconditionally rather than gated on PATH mode the way `pathPlan` is: a
    // second copy of that condition could drift from it, and over-keying only costs a miss.
    // Its own element, never folded into `curve`, so neither field can absorb the other.
    const base = transformKey(p.resolvedPath?.baseTransform ?? null);
    return [
        'poly',
        Array.from(p.polygon).join(','),
        p.mode,
        p.depth,
        p.spinDegrees,
        p.spinSides,
        p.smoothFaces,
        p.flipFaces,
        p.pathIntervalType,
        p.pathInterval,
        p.pathSimplifyAngle,
        p.pathRotation,
        p.pathRotationAccurate,
        p.pathContinuousU,
        p.pathUDistance,
        p.pathJoined,
        curve,
        base,
    ].join('|');
}
//# sourceMappingURL=csgGeometry.js.map